import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Chip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Select, MenuItem, Checkbox, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Snackbar, Alert, Switch, FormControlLabel,
  Accordion, AccordionSummary, AccordionDetails, CircularProgress, Tooltip,
  Autocomplete,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UndoIcon from '@mui/icons-material/Undo';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { pulseRequest } from '../utils/pulseRequest';
import PriorityTableDialog from './PriorityTable';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';

const authHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Display visual system status colors (match the board)
const SC = { green: '#22A06B', amber: '#F5A524', red: '#E5484D', accent: '#3E63DD', gray: '#98A2B3', track: '#EEF1F6' };
const timeET = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' }) : '—');

const fmtIdle = (idleSince) => {
  if (!idleSince) return null;
  const mins = Math.floor((Date.now() - new Date(idleSince).getTime()) / 60000);
  return mins < 1 ? '<1m' : `${mins}m`;
};

function Assignments() {
  const navigate = useNavigate();
  const { isAdmin, isManager } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authExpired, setAuthExpired] = useState(false);
  const [selected, setSelected] = useState([]); // employee_ids for bulk assign
  const [bulkGroup, setBulkGroup] = useState('');
  // merged "Pin to…" dialog: Category / OBD / Store / Platform in one flow
  const [pinDialog, setPinDialog] = useState(null); // { employeeId, name, type, value, obdNumber }
  const [storeOptions, setStoreOptions] = useState([]);     // for the Store picker (codes zero-padded)
  const [platformOptions, setPlatformOptions] = useState([]); // OBD platforms for the Platform picker
  const [ptOpen, setPtOpen] = useState(false); // priority table (full page)
  const [logData, setLogData] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [outlookData, setOutlookData] = useState(null);
  const [endDraft, setEndDraft] = useState({}); // employee_id -> 'HH:MM' expected-END being edited
  const [suggestions, setSuggestions] = useState(null); // null = not fetched
  const [departures, setDepartures] = useState(null); // { grace_minutes, departures }
  const [suggTried, setSuggTried] = useState(false);

  const fetchRoster = useCallback(async () => {
    const r = await pulseRequest(`${API_BASE}/api/assignments/roster`, { headers: authHeaders() });
    if (r.sessionExpired) { setAuthExpired(true); return; }
    if (!r.ok) { setError(r.message); setLoading(false); return; }
    setData(r.data);
    setError(null);
    setLoading(false);
  }, []);

  const fetchLog = useCallback(async () => {
    const r = await pulseRequest(`${API_BASE}/api/assignments/log`, { headers: authHeaders() });
    if (r.ok) setLogData(r.data);
  }, []);

  const fetchOutlook = useCallback(async () => {
    const r = await pulseRequest(`${API_BASE}/api/assignments/outlook`, { headers: authHeaders() });
    if (r.ok) setOutlookData(r.data);
  }, []);

  // DEPARTURES PANEL (Oct 6): today's departures by platform — a lead marks
  // a truck gone the moment it leaves; its open OBDs re-rank immediately.
  const fetchDepartures = useCallback(async () => {
    const r = await pulseRequest(`${API_BASE}/api/assignments/departures`, { headers: authHeaders() });
    if (r.ok) setDepartures(r.data);
  }, []);
  useEffect(() => {
    fetchDepartures();
    const t = setInterval(fetchDepartures, 60000);
    return () => clearInterval(t);
  }, [fetchDepartures]);
  const markDeparted = async (d) => {
    const r = await pulseRequest(`${API_BASE}/api/assignments/departures/mark`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ platform: d.platform, scheduled_at: d.scheduled_at }),
    });
    if (r.sessionExpired) { setAuthExpired(true); return; }
    if (!r.ok) { notify(r.message, 'error'); return; }
    notify(`${d.platform} marked departed — open OBDs re-ranked to its next departure (undo within 10 min)`);
    fetchDepartures(); fetchRoster(); fetchOutlook(); fetchLog();
  };
  const undoDeparted = async (d) => {
    const r = await pulseRequest(`${API_BASE}/api/assignments/departures/undo`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ platform: d.platform, scheduled_at: d.scheduled_at }),
    });
    if (r.sessionExpired) { setAuthExpired(true); return; }
    if (!r.ok) { notify(r.message, 'error'); return; }
    notify('Departure mark undone');
    fetchDepartures(); fetchRoster(); fetchOutlook(); fetchLog();
  };

  useEffect(() => {
    fetchRoster();
    fetchLog();
    fetchOutlook();
    const t = setInterval(() => { fetchRoster(); fetchOutlook(); }, 30000);
    return () => clearInterval(t);
  }, [fetchRoster, fetchLog, fetchOutlook]);

  const fetchSuggestions = useCallback(async () => {
    const r = await pulseRequest(`${API_BASE}/api/assignments/suggestions`, { headers: authHeaders() });
    if (r.ok) setSuggestions(r.data.suggestions || []);
  }, []);

  // fresh shift: nobody assigned yet → pre-fill suggestions (not saved until accepted)
  useEffect(() => {
    if (suggTried || !data || !outlookData) return;
    const roster0 = data.roster || [];
    if (roster0.length > 0 && roster0.every(x => !x.group_id)
      && (outlookData.outlook?.departures || []).length > 0) {
      setSuggTried(true);
      fetchSuggestions();
    }
  }, [data, outlookData, suggTried, fetchSuggestions]);

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const act = async (path, body, okMsg) => {
    const r = await pulseRequest(`${API_BASE}/api/assignments/${path}`, {
      method: body?._method || 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
    });
    if (r.sessionExpired) { setAuthExpired(true); return false; }
    if (!r.ok) { notify(r.message, 'error'); return false; }
    if (okMsg) notify(okMsg);
    await fetchRoster();
    fetchLog();
    fetchOutlook();
    return true;
  };

  const setAssociateGroup = async (employeeId, groupId, confirmExcluded = false) => {
    const r = await pulseRequest(`${API_BASE}/api/assignments/associate/${encodeURIComponent(employeeId)}/group`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      // 'floater' from the dropdown → group_id null ("No group (floater)")
      body: JSON.stringify({ group_id: groupId === 'floater' ? null : groupId, ...(confirmExcluded ? { confirm_excluded: true } : {}) }),
    });
    if (r.sessionExpired) { setAuthExpired(true); return; }
    if (!r.ok) {
      // excluded-group home: the backend 409s with a warning until confirmed
      if (r.status === 409 && r.data?.warning) {
        if (window.confirm(r.message)) return setAssociateGroup(employeeId, groupId, true);
        return;
      }
      notify(r.message, 'error'); return;
    }
    notify(groupId === 'floater'
      ? 'Now a floater — they get work from the neediest category each tap'
      : 'Group updated — applies to their next OBD');
    setSuggestions(s => (s || []).filter(x => x.employee_id !== employeeId));
    await fetchRoster();
    fetchLog();
    fetchOutlook();
  };

  const bulkAssign = async (groupId, ids, confirmExcluded = false) => {
    const r = await pulseRequest(`${API_BASE}/api/assignments/bulk-assign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ group_id: Number(groupId), employee_ids: ids, ...(confirmExcluded ? { confirm_excluded: true } : {}) }),
    });
    if (r.sessionExpired) { setAuthExpired(true); return false; }
    if (!r.ok) {
      if (r.status === 409 && r.data?.warning) {
        if (window.confirm(r.message)) return bulkAssign(groupId, ids, true);
        return false;
      }
      notify(r.message, 'error'); return false;
    }
    await fetchRoster();
    fetchLog();
    fetchOutlook();
    return true;
  };

  // the Store/Platform pickers load once, on first dialog open
  useEffect(() => {
    if (!pinDialog) return;
    (async () => {
      if (!storeOptions.length) {
        const r = await pulseRequest(`${API_BASE}/api/store-priority`, { headers: authHeaders() });
        if (r.ok) {
          setStoreOptions((r.data.platforms || []).flatMap(p => p.stores.map(s => ({
            code: s.site_code, label: `${s.site_code} — ${s.site_name || p.platform_name}`,
          }))));
        }
      }
      if (!platformOptions.length) {
        const r = await pulseRequest(`${API_BASE}/api/assignments/priority-table`, { headers: authHeaders() });
        if (r.ok) setPlatformOptions(r.data.platforms || []);
      }
    })();
  }, [pinDialog, storeOptions.length, platformOptions.length]);

  // Associate pins: category (never loaned, replaces the old lock), store /
  // platform (only those OBDs until none left, then auto-release)
  const savePin = async (employeeId, pinType, pinValue) => {
    const r = await pulseRequest(`${API_BASE}/api/assignments/associate/${encodeURIComponent(employeeId)}/pin`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ pin_type: pinType, pin_value: pinValue || undefined }),
    });
    if (r.sessionExpired) { setAuthExpired(true); return; }
    if (!r.ok) { notify(r.message, 'error'); return; }
    notify(pinType ? 'Pin set' : 'Pin removed');
    setPinDialog(null);
    await fetchRoster();
    fetchLog();
    fetchOutlook();
  };

  // "Can't pick" (persistent per associate): never suggested/loaned there,
  // never served that group's OBDs from the queue
  const saveExclusions = async (employeeId, groupIds) => {
    const r = await pulseRequest(`${API_BASE}/api/assignments/associate/${encodeURIComponent(employeeId)}/exclusions`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ group_ids: groupIds }),
    });
    if (r.sessionExpired) { setAuthExpired(true); return; }
    if (!r.ok) { notify(r.message, 'error'); return; }
    notify(groupIds.length ? "Can't-pick list saved" : "Can't-pick list cleared");
    await fetchRoster();
    fetchLog();
  };

  // Full/Part + EXPECTED END TIME per associate per shift anchor — this is
  // what feeds the outlook's labor hours. endHHMM 'HH:MM' | null; reset=true
  // clears a full-timer override back to the clock-in + shift-hours default.
  const saveLaborType = async (employeeId, laborType, endHHMM, reset = false) => {
    const r = await pulseRequest(`${API_BASE}/api/assignments/associate/${encodeURIComponent(employeeId)}/labor`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ labor_type: laborType, expected_end: endHHMM || undefined, reset: reset || undefined }),
    });
    if (r.sessionExpired) { setAuthExpired(true); return; }
    if (!r.ok) { notify(r.message, 'error'); return; }
    notify(reset ? 'Back to the default end time' : endHHMM ? `Expected end set` : (laborType === 'part' ? 'Part time' : 'Full time'));
    setEndDraft(d => { const n = { ...d }; delete n[employeeId]; return n; });
    await fetchRoster();
    fetchLog();
    fetchOutlook();
  };

  const acceptAllSuggestions = async () => {
    const byGroup = {};
    for (const s of suggestions || []) (byGroup[s.group_id] = byGroup[s.group_id] || []).push(s.employee_id);
    for (const [gid, ids] of Object.entries(byGroup)) {
      await bulkAssign(Number(gid), ids);
    }
    notify(`Accepted ${(suggestions || []).length} suggested assignments`);
    setSuggestions([]);
  };

  const toggleEnabled = async (enabled) => {
    const r = await pulseRequest(`${API_BASE}/api/assignments/config`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ ...data.config, enabled }),
    });
    if (r.sessionExpired) { setAuthExpired(true); return; }
    if (!r.ok) { notify(r.message, 'error'); return; }
    notify(enabled ? 'Auto-assignment is ON' : 'Auto-assignment is OFF');
    await fetchRoster();
  };

  if (authExpired) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 12, gap: 2 }}>
        <Typography variant="h5">Your session has expired</Typography>
        <Button variant="contained" onClick={() => navigate('/login')}>Go to Sign In</Button>
      </Box>
    );
  }
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 12 }}><CircularProgress /></Box>;
  }
  if (error || !data) {
    return <Box sx={{ p: 4 }}><Alert severity="error">{error || 'Failed to load'}</Alert></Box>;
  }

  const groups = data.groups || [];
  const roster = data.roster || [];
  const groupById = Object.fromEntries(groups.map(g => [g.id, g]));
  const assignedCount = {};
  roster.forEach(r => { if (r.group_id) assignedCount[r.group_id] = (assignedCount[r.group_id] || 0) + 1; });
  // SECTION SPLIT: people currently checked into Picking (category required —
  // red flag if none can be applied) vs everyone else (another activity, or
  // not clocked in) shown dimmed below; a lead can still set a category ahead
  // of time so it applies when they start picking.
  const pickingRoster = roster.filter(r => r.in_picking);
  const othersRoster = roster.filter(r => !r.in_picking);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>Assignments</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">Business day {data.business_date}</Typography>
            {data.shift?.setup_window && (
              <Chip size="small" color="info" label={`Pre-shift setup — staging the ${new Date(data.shift.target).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' })} ET shift`} />
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* ADMIN-ONLY toggle (kill switch included): managers see the
              state read-only */}
          <FormControlLabel
            control={
              <Switch
                checked={!!data.config?.enabled}
                onChange={(e) => toggleEnabled(e.target.checked)}
                disabled={!isAdmin}
              />
            }
            label={`Auto-assignment ${data.config?.enabled ? 'ON' : 'OFF'}${isManager && !isAdmin ? ' (admin-only setting)' : ''}`}
          />
          <Button variant="outlined" onClick={() => setPtOpen(true)}>
            View priority table
          </Button>
          <Button variant="outlined" onClick={() => act('copy-yesterday', {}, 'Copied the same shift from yesterday (existing rows kept)')}>
            Copy last shift
          </Button>
        </Box>
      </Box>

      {/* Shift outlook banner (engine: lib/shiftOutlook.js, shared with the
          Display). Labor comes from the roster below — full/part time hours
          per associate; no FTE entry any more. */}
      {(() => {
        const o = outlookData?.outlook;
        const meta = {
          between_shifts: { label: 'Between shifts', c: SC.gray },
          on_track: { label: 'On track', c: SC.green }, tight: { label: 'Tight', c: SC.amber },
          wont_make_it: { label: "Won't make it", c: SC.red }, no_open: { label: 'No open departures', c: SC.gray },
        }[o?.overall || 'no_open'];
        const between = o?.state === 'between_shifts';
        const worst = o ? (o.departures.find(d => d.status === o.overall) || o.departures[0]) : null;
        const short = worst && worst.short_labor_hours > 0
          ? `short ${worst.short_labor_hours} labor hours${worst.short_people != null ? ` (about ${worst.short_people} ${worst.short_people === 1 ? 'person' : 'people'})` : ''}`
          : null;
        const cpName = worst
          ? (worst.is_shift_end ? `End of shift (${timeET(worst.departure_at)})` : `${timeET(worst.departure_at)} truck`)
          : '';
        const endRow = o ? o.departures.find(d => d.is_shift_end) : null;
        // priority-first covering: name the categories that will be missed
        const missBit = worst && (worst.missed_groups || []).length
          ? ` · will miss: ${worst.missed_groups.join(', ')}` : '';
        let sentence = 'No open departures this shift.';
        if (between) {
          sentence = `${o.next_shift?.name} starts ${timeET(o.next_shift?.start_at)}`;
        } else if (o && worst) {
          if (o.overall === 'wont_make_it') {
            sentence = worst.past_due
              ? `${cpName}: ${worst.cum_std_hours} labor hours of work still open${missBit}`
              : `${cpName}: ${short || 'behind'}${missBit}`;
          } else if (o.overall === 'tight') {
            sentence = `${cpName}: tight${short ? ` — ${short}` : ''}${missBit}`;
          } else if (o.overall === 'on_track') {
            sentence = 'All trucks on track';
          }
        }
        const basis = between
          ? `Waiting: ${o.waiting?.obds ?? 0} OBDs · ${(o.waiting?.lines ?? 0).toLocaleString()} lines · ${o.waiting?.std_hours ?? 0} std hours`
          : o
          ? `${o.inputs.labor_source === 'roster'
              ? `Based on ${o.inputs.labor_hours} labor hours`
              : `No shift hours set — using ${o.inputs.headcount} clocked in`} · assumes ${o.inputs.pace_pct}% pace${o.inputs.pace_source === 'shift' ? " (tonight's pace)" : o.inputs.pace_source === 'avg_30d' ? ' (30-day avg)' : ''}`
          : '';
        return (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ minWidth: 260, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: meta.c }}>{meta.label}</Typography>
                  <Typography variant="h6" sx={{ color: '#101828' }}>{sentence}</Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#667085', mt: 0.5 }}>{basis}</Typography>
                {endRow && (
                  <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600, color: endRow.status === 'on_track' ? SC.green : endRow.status === 'tight' ? SC.amber : SC.red }}>
                    End of shift ({timeET(endRow.departure_at)}): {endRow.status === 'on_track' ? 'On track' : endRow.status === 'tight' ? 'Tight' : "Won't make it"}
                    {endRow.short_labor_hours > 0 ? ` · short ${endRow.short_labor_hours} labor hours` : ''}
                  </Typography>
                )}
                {(o?.inputs?.idle_pickers || 0) > 0 && (
                  <Typography variant="body2" sx={{ color: '#B54708', mt: 0.5, fontWeight: 600 }}>
                    {o.inputs.idle_pickers} of {o.inputs.headcount} pickers idle {o.inputs.idle_warning_minutes}+ min
                  </Typography>
                )}
                {/* TRUCK TRIAGE (Oct 9, toggle): the queue is protecting the
                    next truck — Mark departed or a platform boost override */}
                {outlookData?.triage?.active && (
                  <Typography variant="body2" sx={{ color: '#B54708', mt: 0.5, fontWeight: 700 }}>
                    Truck triage: {outlookData.triage.text}
                  </Typography>
                )}
                {(o?.warnings || []).map(w => (
                  <Typography key={w.group_id ?? w.type} variant="body2" sx={{ color: w.type === 'config' ? '#B54708' : SC.red, mt: 0.5, fontWeight: 600 }}>
                    {w.message}{w.type === 'config' ? ' (admin: Configuration → Labor Standards)' : ' ET'}
                  </Typography>
                ))}
                {!between && (data?.part_timers_missing_end || 0) > 0 && (
                  <Typography variant="body2" sx={{ color: '#B54708', mt: 0.5, fontWeight: 600 }}>
                    {data.part_timers_missing_end} part-timer{data.part_timers_missing_end === 1 ? '' : 's'} missing end time{data.part_timers_missing_end === 1 ? '' : 's'} — counted at the full-time default until set
                  </Typography>
                )}
              </Box>
              {o?.inputs?.labor_source === 'roster' && (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>{o.inputs.labor_hours}h</Typography>
                  <Typography variant="caption" color="text.secondary">available labor · {o.inputs.headcount} picking</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        );
      })()}

      {/* Active loans — who PULSE has moved, why, and when they come back */}
      {(() => {
        const loans = (data.roster || []).filter(r => r.loan_group_id);
        if (!loans.length) return null;
        return (
          <Paper sx={{ p: 1.5, mb: 2, border: `1px dashed ${SC.amber}`, bgcolor: '#FFFBF2' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Active loans</Typography>
            {loans.map(r => (
              <Typography key={r.employee_id} variant="body2">
                {r.name || r.employee_id} → {groupById[r.loan_group_id]?.name || '?'} (home {groupById[r.group_id]?.name || '?'})
                {r.loan_reason ? ` · ${r.loan_reason}` : ''} · returns when covered
              </Typography>
            ))}
          </Paper>
        );
      })()}

      {/* Group strip: remaining work + assigned headcount per group */}
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        {groups.map(g => {
          const og = (outlookData?.outlook?.groups || []).find(x => x.group_id === g.id);
          const needed = og?.needed_heads || 0;
          const assigned = assignedCount[g.id] || 0;
          const hasWork = (g.std_hours || 0) > 0.05 || g.obds > 0;
          // PRIMARY = slack hours at the earliest open departure (one labor
          // model everywhere); heads stay as secondary info
          const slack = og?.slack_hours ?? null;
          const color = !hasWork ? SC.gray
            : slack == null ? (assigned === 0 && needed > 0 ? SC.red : SC.gray)
            : slack < -0.25 ? SC.red
            : slack < 0.25 ? SC.amber : SC.green;
          return (
            <Grid item xs={6} sm={4} md={2} lg={true} key={g.id}>
              <Card variant="outlined" sx={{ height: '100%', borderColor: color === SC.gray ? undefined : color }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>{g.name}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                    <Typography variant="h6" sx={{ color: color === SC.gray ? 'text.secondary' : color, fontWeight: 700 }}>
                      {slack == null ? '—' : `${slack > 0 ? '+' : ''}${slack}h`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {og?.slack_departure_at ? `by ${timeET(og.slack_departure_at)}` : 'slack'}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" noWrap>
                    {assigned}/{needed} heads · {g.obds} OBDs{g.std_hours ? ` · ${g.std_hours}h` : ''}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Departures panel (Oct 6): status per platform-departure; Mark
          departed re-ranks that truck's open OBDs to its next departure
          immediately; undo within 10 minutes; unmarked trucks auto-depart
          at scheduled + grace. */}
      {departures && (departures.departures || []).length > 0 && (
        <Paper sx={{ p: 1.5, mb: 2 }}>
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
            Departures
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              unmarked trucks auto-depart {departures.grace_minutes} min after schedule
            </Typography>
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {(departures.departures || []).map((d) => (
              <Box key={`${d.platform}|${d.scheduled_at}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 86 }}>{timeET(d.scheduled_at)}</Typography>
                <Typography variant="body2" sx={{ minWidth: 110 }}>{d.platform}</Typography>
                <Chip size="small" label={d.status === 'departed' ? 'Departed' : d.status === 'due' ? 'Due' : 'Upcoming'}
                  color={d.status === 'due' ? 'warning' : 'default'}
                  variant={d.status === 'departed' ? 'filled' : 'outlined'} />
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70 }}>
                  {d.open_obds > 0 ? `${d.open_obds} open` : '—'}
                </Typography>
                {d.status === 'departed' && (
                  <Typography variant="caption" color="text.secondary">
                    left {d.late_minutes > 0 ? `${d.late_minutes} min late` : d.late_minutes < 0 ? `${-d.late_minutes} min early` : 'on time'}
                    {d.auto ? ' · auto' : d.marked_by ? ` · by ${d.marked_by}` : ''}
                  </Typography>
                )}
                {d.status !== 'departed' && (
                  <Button size="small" variant="outlined" onClick={() => markDeparted(d)}>Mark departed</Button>
                )}
                {d.undoable && (
                  <Button size="small" color="warning" onClick={() => undoDeparted(d)}>Undo</Button>
                )}
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Suggestions bar */}
      {(suggestions?.length > 0) && (
        <Paper sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 2, border: `1px dashed ${SC.accent}`, bgcolor: '#F5F8FF' }}>
          <Typography sx={{ fontWeight: 600 }}>{suggestions.length} suggested assignment{suggestions.length === 1 ? '' : 's'}</Typography>
          <Typography variant="body2" color="text.secondary">Nothing is saved until you accept.</Typography>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button size="small" variant="contained" onClick={acceptAllSuggestions}>Accept all</Button>
            <Button size="small" onClick={fetchSuggestions}>Re-suggest</Button>
            <Button size="small" onClick={() => setSuggestions([])}>Dismiss</Button>
          </Box>
        </Paper>
      )}

      {/* Bulk assign bar */}
      {selected.length > 0 && (
        <Paper sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#e3f2fd' }}>
          <Typography>{selected.length} selected</Typography>
          <Select size="small" value={bulkGroup} onChange={(e) => setBulkGroup(e.target.value)} displayEmpty sx={{ minWidth: 180 }}>
            <MenuItem value="" disabled>Choose group…</MenuItem>
            {groups.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
          </Select>
          <Button
            variant="contained"
            disabled={!bulkGroup}
            onClick={async () => {
              const ok = await bulkAssign(bulkGroup, selected);
              if (ok) { notify(`Assigned ${selected.length} associates`); setSelected([]); setBulkGroup(''); }
            }}
          >
            Assign selected
          </Button>
          <Button onClick={() => setSelected([])}>Clear</Button>
        </Paper>
      )}

      {/* Roster */}
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Associate</TableCell>
              <TableCell>Group (applies to next OBD)</TableCell>
              <TableCell>Shift</TableCell>
              <TableCell>Clock-in</TableCell>
              <TableCell>Expected end</TableCell>
              <TableCell align="right">Hours left</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Can&apos;t pick</TableCell>
              <TableCell>Pin to</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roster.length === 0 && (
              <TableRow><TableCell colSpan={10}><Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No one is checked in right now.</Typography></TableCell></TableRow>
            )}
            {[...pickingRoster, ...othersRoster].map(r => {
              const idle = !r.current_obd && !r.on_break ? fmtIdle(r.idle_since) : null;
              const dimmed = !r.in_picking;
              const firstDim = dimmed && othersRoster[0] && r.employee_id === othersRoster[0].employee_id;
              return (
                <React.Fragment key={r.employee_id}>
                {firstDim && (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ bgcolor: '#FAFAFA', py: 0.75 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#667085', letterSpacing: 0.4 }}>
                        NOT IN PICKING
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#98A2B3', ml: 1 }}>
                        another activity or not clocked in — category optional, applies when they start picking
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                <TableRow hover sx={dimmed ? { opacity: 0.55 } : undefined}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selected.includes(r.employee_id)}
                      onChange={() => setSelected(s => s.includes(r.employee_id) ? s.filter(x => x !== r.employee_id) : [...s, r.employee_id])}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.name || 'Unknown'}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.employee_id}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Select
                        size="small"
                        value={r.source === 'floater' ? 'floater' : (r.group_id ?? '')}
                        onChange={(e) => setAssociateGroup(r.employee_id, e.target.value)}
                        displayEmpty
                        sx={{ minWidth: 170 }}
                      >
                        <MenuItem value="" disabled><em>No group</em></MenuItem>
                        {/* deliberate no-group: floats to the neediest
                            category on every tap (never loaned) */}
                        <MenuItem value="floater"><em>No group (floater)</em></MenuItem>
                        {groups.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
                      </Select>
                      {r.source === 'floater' && (
                        <Tooltip title="Floats to the neediest category on every tap — exclusions and all queue rules still apply; never loaned." arrow>
                          <Chip size="small" label="Floater" sx={{ height: 18, fontSize: 11, bgcolor: '#EEF2FF', color: SC.accent }} />
                        </Tooltip>
                      )}
                      {r.group_id && r.source === 'auto_suggested' && (
                        <Tooltip title={`Applied from their 30-day history${r.history_basis ? ` — ${r.history_basis.obds} OBDs in last 30 days` : ''}. Confirm it or choose another group.`} arrow>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip size="small" label="Auto-suggested" sx={{ height: 18, fontSize: 11, bgcolor: '#FFF4E5', color: '#B54708' }} />
                            <Button size="small" sx={{ minWidth: 0, px: 0.75 }} onClick={() => setAssociateGroup(r.employee_id, r.group_id)}>Confirm</Button>
                          </Box>
                        </Tooltip>
                      )}
                      {/* in Picking, no group, and no 30d history to apply —
                          the lead must choose */}
                      {!r.group_id && r.needs_group && (
                        <Tooltip title="Currently picking with no category and no qualifying 30-day history — choose one." arrow>
                          <Chip size="small" color="error" label="Needs category" sx={{ fontWeight: 600 }} />
                        </Tooltip>
                      )}
                      {!r.group_id && (() => {
                        const sug = (suggestions || []).find(s => s.employee_id === r.employee_id);
                        if (!sug) return null;
                        return (
                          <Tooltip title={sug.reason?.text || ''} arrow>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.4, border: `1.5px dashed ${SC.accent}`, borderRadius: 2 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: SC.accent }}>{sug.group_name}</Typography>
                              <Chip size="small" label="Suggested" sx={{ height: 18, fontSize: 11, bgcolor: '#EEF2FF', color: SC.accent }} />
                              <Button size="small" sx={{ minWidth: 0, px: 0.75 }} onClick={() => setAssociateGroup(r.employee_id, sug.group_id)}>Accept</Button>
                            </Box>
                          </Tooltip>
                        );
                      })()}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {/* Full/Part — switching type clears any end override */}
                    <Select
                      size="small"
                      value={r.labor_type || 'full'}
                      onChange={(e) => saveLaborType(r.employee_id, e.target.value, null)}
                      sx={{ minWidth: 110 }}
                    >
                      <MenuItem value="full">Full time</MenuItem>
                      <MenuItem value="part">Part time</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{r.clock_in_at ? timeET(r.clock_in_at) : '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    {/* Expected END time: gray default, normal override with
                        who/when + Reset, amber Set-end-time for part-timers */}
                    {endDraft[r.employee_id] !== undefined ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TextField
                          size="small" type="time" sx={{ width: 130 }}
                          inputProps={{ step: 900 }}
                          value={endDraft[r.employee_id]}
                          onChange={(e) => setEndDraft(d => ({ ...d, [r.employee_id]: e.target.value }))}
                        />
                        <Button size="small" disabled={!endDraft[r.employee_id]}
                          onClick={() => saveLaborType(r.employee_id, r.labor_type || 'full', endDraft[r.employee_id])}>Set</Button>
                        <Button size="small" onClick={() => setEndDraft(d => { const n = { ...d }; delete n[r.employee_id]; return n; })}>✕</Button>
                      </Box>
                    ) : r.expected_end_source === 'missing' && (r.labor_type || 'full') === 'full' ? (
                      // full-timer with NO computable default (shift hours
                      // unset): never invite hand-entry — point admins at
                      // the config instead
                      <Typography variant="body2" sx={{ color: '#98A2B3' }}>
                        Needs shift hours
                        <Typography component="span" variant="caption" sx={{ color: '#98A2B3', display: 'block' }}>
                          admin: Configuration → Labor Standards
                        </Typography>
                      </Typography>
                    ) : r.expected_end_source === 'missing' ? (
                      <Box>
                        <Button size="small" color="warning" variant="outlined"
                          onClick={() => setEndDraft(d => ({ ...d, [r.employee_id]: '' }))}>
                          Set end time
                        </Button>
                        {r.fit_uses_shift_end && (
                          <Typography variant="caption" sx={{ color: '#B54708', display: 'block' }}>
                            fit check uses shift end
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={r.expected_end_source === 'default'
                          ? { color: '#98A2B3' } : { fontWeight: 600 }}>
                          {r.expected_end ? timeET(r.expected_end) : '—'}
                        </Typography>
                        {r.expected_end_source === 'default' && r.expected_end && (
                          <Typography variant="caption" sx={{ color: '#98A2B3' }}>default</Typography>
                        )}
                        {(r.expected_end_source === 'override' || r.expected_end_source === 'entered') && (
                          <Typography variant="caption" color="text.secondary">
                            set by {r.expected_end_set_by || '—'}{r.expected_end_set_at ? ` · ${timeET(r.expected_end_set_at)}` : ''}
                          </Typography>
                        )}
                        <Button size="small" sx={{ minWidth: 0, px: 0.5 }}
                          onClick={() => setEndDraft(d => ({ ...d, [r.employee_id]: '' }))}>Edit</Button>
                        {r.expected_end_source === 'override' && (
                          <Button size="small" sx={{ minWidth: 0, px: 0.5 }}
                            onClick={() => saveLaborType(r.employee_id, r.labor_type || 'full', null, true)}>
                            Reset to default
                          </Button>
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {r.remaining_hours != null ? `${r.remaining_hours}h` : '—'}
                    </Typography>
                    {!r.picking && <Typography variant="caption" color="text.secondary">not picking</Typography>}
                  </TableCell>
                  <TableCell>
                    {r.clocked_in === false ? (
                      <Chip size="small" label="Not clocked in" variant="outlined" sx={{ color: '#98A2B3' }} />
                    ) : r.on_break ? (
                      <Chip size="small" label={r.on_break === 'lunch' ? 'Lunch' : 'Break'} color="warning" variant="outlined" />
                    ) : r.current_obd ? (
                      <Chip size="small" label={`OBD ${r.current_obd}`} color="primary" variant="outlined" />
                    ) : (
                      <Chip size="small" label={idle ? `Idle ${idle}` : 'Idle'} variant="outlined" />
                    )}
                    {r.blocked_obd && (
                      <Tooltip title="They tapped Start while holding an unfinished OBD — have them finish it, or pause it.">
                        <Chip size="small" color="error" sx={{ ml: 0.5 }} label={`Blocked · OBD ${r.blocked_obd}`} />
                      </Tooltip>
                    )}
                    {r.auto_paused_obd && (
                      <Tooltip title="Paused automatically when they left picking with this OBD in progress — resume it (Activity Monitor) or reassign the work.">
                        <Chip size="small" color="warning" sx={{ ml: 0.5 }} label={`Auto-paused · OBD ${r.auto_paused_obd}`} />
                      </Tooltip>
                    )}
                    {/* loans live HERE now — they're the rebalancer's doing,
                        not a lead setting */}
                    {r.loan_group_id && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Tooltip title={r.loan_reason || ''} arrow>
                          <Chip size="small" sx={{ bgcolor: '#FFF4E5', color: '#B54708', fontWeight: 600 }}
                            label={`On loan → ${groupById[r.loan_group_id]?.name || '?'}`} />
                        </Tooltip>
                        <Button size="small" startIcon={<UndoIcon />} sx={{ minWidth: 0, px: 0.5 }}
                          onClick={() => act(`associate/${encodeURIComponent(r.employee_id)}/end-loan`, {}, 'Loan ended — back home')}>
                          Return now
                        </Button>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {/* persistent per associate (survives shift flips): the
                        queue, loans and suggestions all skip these groups */}
                    <Select
                      multiple
                      size="small"
                      displayEmpty
                      value={r.excluded_group_ids || []}
                      onChange={(e) => {
                        // the two bulk rows carry sentinel values
                        const v = e.target.value;
                        if (v.includes('__all__')) return saveExclusions(r.employee_id, groups.map(g => g.id));
                        if (v.includes('__none__')) return saveExclusions(r.employee_id, []);
                        saveExclusions(r.employee_id, v);
                      }}
                      renderValue={(sel) => sel.length
                        ? sel.map(id => groupById[id]?.name || id).join(', ')
                        : <em style={{ color: '#98A2B3' }}>None</em>}
                      sx={{ minWidth: 120, maxWidth: 180 }}
                    >
                      <MenuItem value="__all__" dense><em>Select all</em></MenuItem>
                      <MenuItem value="__none__" dense><em>Deselect all</em></MenuItem>
                      {groups.map(g => (
                        <MenuItem key={g.id} value={g.id}>
                          <Checkbox size="small" checked={(r.excluded_group_ids || []).includes(g.id)} sx={{ py: 0 }} />
                          {g.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {(r.excluded_group_ids || []).length >= groups.length && groups.length > 0 && (
                      <Typography variant="caption" sx={{ color: '#E5484D', display: 'block', fontWeight: 600 }}>
                        Can&apos;t be assigned anything
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {/* merged "Pin to" cell: the single associate pin
                        (category/store/platform — choosing a new one
                        replaces it) plus any OBD-level pins (a lead may
                        queue several; each chip removes its own) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      {r.pin_type && (
                        <Tooltip title={`Set by ${r.pin_set_by || '—'}${r.pin_set_at ? ` · ${timeET(r.pin_set_at)}` : ''}${r.pin_type === 'category' ? ' · never loaned, until removed' : ' · auto-releases when nothing is left'}`} arrow>
                          <Chip
                            size="small" color="info"
                            label={r.pin_type === 'category'
                              ? (groupById[Number(r.pin_value)]?.name || 'Category')
                              : r.pin_type === 'store' ? `Store ${r.pin_value}` : r.pin_value}
                            onDelete={() => savePin(r.employee_id, null)}
                          />
                        </Tooltip>
                      )}
                      {(r.pinned_obds || []).map(p => (
                        <Chip
                          key={p.id} size="small" variant="outlined" color="info"
                          label={`OBD #${String(p.obd_number).replace(/^0+/, '')}`}
                          onDelete={async () => {
                            const rr = await pulseRequest(`${API_BASE}/api/obd/${p.id}/unclaim`, { method: 'POST', headers: authHeaders() });
                            if (rr.sessionExpired) { setAuthExpired(true); return; }
                            if (!rr.ok) { notify(rr.message, 'error'); return; }
                            notify('OBD pin removed');
                            fetchRoster(); fetchLog();
                          }}
                        />
                      ))}
                      <Button size="small" sx={{ minWidth: 0, px: 0.75 }}
                        onClick={() => setPinDialog({ employeeId: r.employee_id, name: r.name, type: 'category', value: null, obdNumber: '' })}>
                        {r.pin_type || (r.pinned_obds || []).length ? '+' : 'Pin to…'}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Day stats + log */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 600 }}>Today&apos;s assignment stats &amp; log</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Per associate (auto-assigned today)</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Associate</TableCell>
                    <TableCell align="right">OBDs</TableCell>
                    <TableCell align="right">Avg lines</TableCell>
                    <TableCell align="right">60+ line OBDs</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(logData?.day_stats || []).map(s => (
                    <TableRow key={s.employee_id}>
                      <TableCell>{s.name || s.employee_id}</TableCell>
                      <TableCell align="right">{s.obds_assigned}</TableCell>
                      <TableCell align="right">{s.avg_lines}</TableCell>
                      <TableCell align="right">{s.big_obds}</TableCell>
                    </TableRow>
                  ))}
                  {!(logData?.day_stats || []).length && (
                    <TableRow><TableCell colSpan={4}><Typography variant="caption" color="text.secondary">No auto-assigned OBDs yet today.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Grid>
            <Grid item xs={12} md={6}>
              {/* The popup button is gone (Oct 1) — live rows now come only
                  from Activity-Monitor lunch/break/clock-out taps releasing a
                  just-started (<150s) OBD; old 'release_button' rows are
                  historical. High counts = dumping just-started OBDs. */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>OBD releases under 150s (Activity Monitor; older rows incl. the removed button)</Typography>
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Associate</TableCell>
                    <TableCell align="right">This shift</TableCell>
                    <TableCell align="right">Last 7 days</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(logData?.release_stats || []).map(s => (
                    <TableRow key={s.employee_id}>
                      <TableCell>{s.name || s.employee_id}</TableCell>
                      <TableCell align="right">{s.shift_count}</TableCell>
                      <TableCell align="right">{s.week_count}</TableCell>
                    </TableRow>
                  ))}
                  {!(logData?.release_stats || []).length && (
                    <TableRow><TableCell colSpan={3}><Typography variant="caption" color="text.secondary">No releases in the last 7 days.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Change log</Typography>
              <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                {(logData?.log || []).map(l => (
                  <Typography key={l.id} variant="body2" sx={{ py: 0.25 }}>
                    <Typography component="span" variant="caption" color="text.secondary">
                      {new Date(l.created_at).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' })} ET —{' '}
                    </Typography>
                    {l.associate || l.employee_id}: {l.from_group || '—'} → {l.to_group || '—'} ({l.reason}, by {l.changed_by})
                  </Typography>
                ))}
                {!(logData?.log || []).length && (
                  <Typography variant="caption" color="text.secondary">No changes logged today.</Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Priority table — exact assignment order per group, drag to reorder */}
      <PriorityTableDialog open={ptOpen} onClose={() => { setPtOpen(false); fetchRoster(); fetchLog(); }} />

      {/* Merged "Pin to…" dialog: Category / OBD / Store / Platform — the
          same flows and validation as before, in one control. Category/
          store/platform replace the single associate pin; OBD pins ADD
          another queued OBD (several allowed, unchanged behavior). */}
      <Dialog open={!!pinDialog} onClose={() => setPinDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Pin {pinDialog?.name || 'associate'} to…</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Category: they stay in their group — never loaned — until removed.
            OBD: that OBD jumps their queue (must be Available).
            Store / Platform: only those OBDs until none are left, then it releases itself.
            Their &ldquo;Can&apos;t pick&rdquo; list always applies.
          </Typography>
          <Select
            fullWidth size="small" sx={{ mb: 2 }}
            value={pinDialog?.type || 'category'}
            onChange={(e) => setPinDialog(d => ({ ...d, type: e.target.value, value: null, obdNumber: '' }))}
          >
            <MenuItem value="category">Category (keep in their group)</MenuItem>
            <MenuItem value="obd">OBD (jumps their queue)</MenuItem>
            <MenuItem value="store">Store (ship-to)</MenuItem>
            <MenuItem value="platform">Platform</MenuItem>
          </Select>
          {pinDialog?.type === 'obd' && (
            <TextField
              autoFocus fullWidth size="small" label="OBD number"
              value={pinDialog?.obdNumber || ''}
              onChange={(e) => setPinDialog(d => ({ ...d, obdNumber: e.target.value }))}
            />
          )}
          {pinDialog?.type === 'store' && (
            <Autocomplete
              options={storeOptions}
              value={pinDialog?.value}
              onChange={(e, v) => setPinDialog(d => ({ ...d, value: v }))}
              getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              renderInput={(p) => <TextField {...p} autoFocus size="small" label="Store (code or name)" />}
            />
          )}
          {pinDialog?.type === 'platform' && (
            <TextField
              select fullWidth size="small" label="Platform"
              value={pinDialog?.value || ''}
              onChange={(e) => setPinDialog(d => ({ ...d, value: e.target.value }))}
            >
              {platformOptions.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPinDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              pinDialog?.type === 'obd' ? !pinDialog?.obdNumber?.trim()
                : pinDialog?.type === 'store' ? !pinDialog?.value
                : pinDialog?.type === 'platform' ? !pinDialog?.value
                : false
            }
            onClick={async () => {
              const d = pinDialog;
              if (d.type === 'obd') {
                const ok = await act('override-obd',
                  { employee_id: d.employeeId, obd_number: d.obdNumber.trim() },
                  'OBD pinned — it will be their next assignment');
                if (ok) setPinDialog(null);
                return;
              }
              const value = d.type === 'store' ? (d.value?.code || d.value) : d.value;
              savePin(d.employeeId, d.type, d.type === 'category' ? undefined : String(value).trim());
            }}
          >
            Set pin
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Assignments;
